import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnInit,
    Optional,
    Output,
    Self,
    ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import Quill from 'quill';
import Delta from 'quill-delta';
import { Subject, Subscription } from 'rxjs';

@Component({
    selector: 'ui-rich-text',
    templateUrl: './rich-text.template.html',
    styleUrls: ['./rich-text.styles.scss'],
    providers: [
        {
            provide: MatFormFieldControl,
            useExisting: RichTextComponent,
        },
    ],
    host: {
        '[id]': 'id',
        '[attr.aria-describedby]': 'describedBy',
    },
})
export class RichTextComponent implements OnInit, OnChanges, ControlValueAccessor, MatFormFieldControl<string> {
    @Output() changed: EventEmitter<any> = new EventEmitter();
    @ViewChild('container', { read: ElementRef }) container: ElementRef;
    static nextId = 0;
    stateChanges = new Subject<void>();
    editor: Quill;
    focused = false;
    touched = false;
    controlType = 'rich-text';
    onChange = (_: any) => {};
    onTouched = () => {
        this.touched = true;
    };
    id = `rich-text-input-${RichTextComponent.nextId++}`;
    describedBy = '';

    @HostBinding('class.floating')
    get shouldLabelFloat() {
        return this.focused || !this.empty;
    }

    @Input()
    readOnly = false;

    @Input() displayToolbar = true;

    @Input() useMiniToolbar = false;

    @Input() displayColorOptions = false;

    @Input() stripNewlines = false;

    @Input() get errorState(): boolean {
        return this._defaultErrorStateMatcher.isErrorState(this.ngControl.control as FormControl, this._parentForm);
    }

    @Input()
    get required() {
        return this._required;
    }

    set required(req) {
        this._required = coerceBooleanProperty(req);
        this.stateChanges.next();
    }

    @Input()
    get placeholder() {
        return this._placeholder;
    }

    set placeholder(plh) {
        this._placeholder = plh;
        this.stateChanges.next();
    }

    @Input()
    get disabled(): boolean {
        return this._disabled;
    }
    set disabled(value: boolean) {
        this._disabled = coerceBooleanProperty(value);
        value ? this.editor.disable() : this.editor.enable();
        this.stateChanges.next();
    }

    @Input()
    get height(): string {
        return this._height;
    }
    set height(value: string) {
        this._height = value;
        this.stateChanges.next();
    }

    get value(): any {
        return this._value;
    }
    set value(value: any) {
        this._value = value;
        this.editor.setContents(this._value);
        this.onChange(value);
        this.stateChanges.next();
    }

    get empty(): boolean {
        return this.editor?.getText().trim() ? false : true;
    }

    private _value: any;
    private _placeholder: string;
    private _required = false;
    private _disabled = false;
    private _height = '200px';
    private _subscriptions: Subscription[] = [];

    defaultToolbarElements = [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link'],
    ];

    miniToolbarElements = [['bold', 'italic'], ['link']];

    constructor(
        @Optional() @Self() public ngControl: NgControl,
        @Optional() public _parentForm: NgForm,
        public _defaultErrorStateMatcher: ErrorStateMatcher,
        private fm: FocusMonitor,
        private elementRef: ElementRef
    ) {
        if (this.ngControl != null) {
            this.ngControl.valueAccessor = this;
        }

        // For MatFormField focus behaviour
        const fmMonitorSub = this.fm.monitor(elementRef.nativeElement, true).subscribe((origin) => {
            this.focused = !!origin;
            this.stateChanges.next();
        });

        // Need to do this to show mat-errors when form is submitted.
        const formSubscription = this._parentForm.ngSubmit.subscribe(() => {
            this.stateChanges.next();
        });

        this._subscriptions = [fmMonitorSub, formSubscription];
    }

    ngOnInit(): void {
        const editor = this.elementRef.nativeElement.querySelector('.editor');

        // This will make Quill use <div> instead of <p> tag for text.
        // The reason why we are adding this is because Quill formatting is not showing properly for gmail.
        if (this.stripNewlines) {
            const block = Quill.import('blots/block');
            block.tagName = 'div';
            Quill.register(block, true);
        }

        if (this.useMiniToolbar) {
            this.defaultToolbarElements = this.miniToolbarElements;
        }
        const toolbar: Array<Array<string | Record<string, primitive | primitive[]>>> = this.defaultToolbarElements;
        if (this.displayColorOptions) {
            toolbar.push([{ color: [] }]);
        }

        this.editor = new Quill(editor, {
            modules: {
                toolbar: this.displayToolbar ? toolbar : false,
            },
            theme: 'snow',
            placeholder: this.placeholder ? this.placeholder : null,
            formats: [
                'background',
                'bold',
                'color',
                'font',
                'code',
                'italic',
                'link',
                'size',
                'strike',
                'script',
                'underline',
                'blockquote',
                'header',
                'indent',
                'list',
                'align',
                'direction',
                'code-block',
                'formula',
                // 'image'
                // 'video'
            ],
        });

        this.editor.on('editor-change', () => {
            this.onChange(this.getValue());
        });

        // Quill v1's clipboard.convert() uses window.getComputedStyle().display (via
        // isLine()) to detect block elements and insert '\n' block separators between
        // them. App-level CSS resets cause h1–h6, p, and li to report non-block display
        // values inside the off-screen .ql-clipboard container, so matchNewline skips
        // the separator and content from adjacent block elements bleeds together.
        //
        // This matcher runs after all built-in Node.ELEMENT_NODE matchers (matchNewline,
        // matchBlot, etc.) and guarantees block elements always end with a '\n' carrying
        // the correct line format.
        this.editor.clipboard.addMatcher(Node.ELEMENT_NODE, (node: Element, delta: Delta) => {
            if (!delta.ops.length) return delta;
            const headingLevel: Record<string, number> = { H1: 1, H2: 2, H3: 3, H4: 4, H5: 5, H6: 6 };
            const heading = headingLevel[node.tagName];
            const isP = node.tagName === 'P';
            const isLI = node.tagName === 'LI';
            if (!heading && !isP && !isLI) return delta;
            let format: Record<string, unknown> | null = null;
            if (heading) {
                format = { header: heading };
            } else if (isLI) {
                format = { list: node.parentElement?.tagName === 'OL' ? 'ordered' : 'bullet' };
            }
            const lastOp = delta.ops[delta.ops.length - 1];
            const endsWithNewline = typeof lastOp.insert === 'string' && lastOp.insert.endsWith('\n');
            if (endsWithNewline) {
                return format ? delta.compose(new Delta().retain(delta.length() - 1).retain(1, format)) : delta;
            }
            return format ? new Delta(delta.ops).insert('\n', format) : new Delta(delta.ops).insert('\n');
        });
    }

    writeValue(contents: string): void {
        // Use Quill's clipboard.convert() rather than Angular's DomSanitizer.sanitize()
        // because the Angular sanitizer strips inline style attributes (e.g. color),
        // causing saved colors to disappear when the editor reloads content.
        // Quill's converter preserves allowed formats while still rejecting anything
        // outside its formats list (script, image, video, etc.).
        // Note: Quill v1 always emits EDITOR_CHANGE even for 'silent' source, but does NOT
        // emit TEXT_CHANGE. Angular's pristine/dirty state is driven by the host form's
        // change detection rather than Quill's text-change event, so using 'silent' here
        // is still correct to avoid user-visible undo history entries on load.
        if (contents) {
            const delta = this.editor.clipboard.convert(contents);
            this.editor.setContents(delta, 'silent' as any);
        } else {
            this.editor.setContents([], 'silent' as any);
        }

        this._value = contents ?? '';
    }

    registerOnChange(fn: (v: any) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDescribedByIds(ids: string[]): void {
        this.describedBy = ids.join(' ');
    }

    onContainerClick(event: MouseEvent): void {
        if ((event.target as Element).tagName.toLowerCase() != 'div') {
            this.elementRef.nativeElement.querySelector('div').focus();
        }
    }

    ngOnChanges(): void {
        if (this.editor) {
            this.editor.setContents(this.value);
        }
    }

    ngOnDestroy(): void {
        this.stateChanges.complete();
        this._subscriptions.forEach((s) => s.unsubscribe());
    }

    private getValue(): string | void {
        if (!this.editor) {
            return;
        }

        const delta = this.editor.getContents();
        if (this.isEmpty(delta)) {
            return;
        }

        return this.ensureColorOnSpan(this.editor.root.innerHTML);
    }

    // Quill's StyleAttributor applies color directly to whichever element the format
    // lands on (e.g. <strong style="color: ...">). Backend sanitizers commonly strip
    // inline styles from non-<span> elements, silently dropping the color. This
    // method moves any color style found on a non-span element into a wrapping <span>
    // so the HTML survives sanitization: <strong><span style="color: ...">…</span></strong>.
    private ensureColorOnSpan(html: string): string {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        doc.body.querySelectorAll<HTMLElement>('[style*="color"]:not(span)').forEach((el) => {
            const color = el.style.color;
            if (!color) return;
            const span = doc.createElement('span');
            span.style.color = color;
            el.style.removeProperty('color');
            if (!el.getAttribute('style')) el.removeAttribute('style');
            while (el.firstChild) span.appendChild(el.firstChild);
            el.appendChild(span);
        });
        return doc.body.innerHTML;
    }

    private isEmpty(contents: Delta): boolean {
        if (contents.ops?.length > 1) {
            return false;
        }

        const opsTypes: string[] = Object.keys(contents.ops[0]);

        if (opsTypes.length > 1) {
            return false;
        }

        if (opsTypes[0] !== 'insert') {
            return false;
        }

        if (contents.ops[0].insert !== '\n') {
            return false;
        }

        return true;
    }
}
